// src/context/AuthContext.js
//
// AuthContext (frontend)
// ======================
//
// PURPOSE
// -------
// UI-facing authentication coordinator for the SPA.
//
// Refresh is NOT owned here.
// Refresh is owned by apiClient.js.
//
// This file owns ONLY:
// - React auth state
// - local tab cleanup
// - explicit login / logout flows
// - scoped cross-tab logout synchronization
// - socket reconnect/disconnect orchestration
// - "prepare this tab for logging in as another user"
// - strict tab-selected bootstrap via backend /auth/check-auth
//
// IMPORTANT SECURITY DECISION
// ---------------------------
// This file implements STRICT TAB-SELECTED AUTH.
//
// That means:
//
// - no live session discovery across tabs
// - no duplicate-tab recovery
// - no lineage/source-tab recovery
// - no registry rehydrate usage
// - no "Continue as current user" behavior
// - no bootstrap auth without THIS TAB already having its own local sessionId
//
// If a tab loses its runtime state, is duplicated, or a URL is opened manually,
// the application must behave conservatively:
//
//   -> user must log in again in THAT tab
//
// This is the safest approach for:
// - shared computers
// - multiple users on one physical device
// - frontend crashes / hard refresh / browser restore
// - deployments where broken runtime state must NOT accidentally expose
//   another user's session context
//
// MULTI-USER ON ONE DEVICE
// ------------------------
// This file is intentionally designed to stay compatible with:
//
// - different users in different tabs on the same browser/device
// - same user in multiple tabs, where each tab explicitly logged in
// - logout scoped to the CURRENT concrete backend session id
//
// Therefore:
// - logout broadcasts are matched primarily by sessionId
// - transport refresh failure clears only THIS tab locally
// - this file never assumes one browser profile == one user
//
// LOGIN PAGE COMPATIBILITY
// ------------------------
// Older UI may still read:
//
//   canOfferRecoveredSessionUi
//
// We keep that field in the context value for compatibility,
// but in this strict model it is ALWAYS false.
//

import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import apiClient, { setAuthToken, setOnTokenUpdate } from "../utils/apiClient";
import { getDeviceId, getBrowserId } from "../utils/clientIdentity";
import {
  setSessionIdentity,
  getCurrentSessionSnapshot,
  getTabId,
  clearTabSessionIdentity,
  hasTabSessionIdentity,
  getSessionId,
} from "../utils/sessionIdentity";
import socketClient from "../utils/socketClient";
import i18n, { normalizeLanguage } from "../i18n";

const DEV = process.env.NODE_ENV !== "production";

const AuthContext = createContext();

/* -------------------------------------------------------------------------- */
/* Dev logging helpers                                                        */
/* -------------------------------------------------------------------------- */

function dlog(message, meta) {
  if (!DEV) return;
  // eslint-disable-next-line no-console
  console.log("[AuthContext]", message, meta || "");
}

function dwarn(message, meta) {
  if (!DEV) return;
  // eslint-disable-next-line no-console
  console.warn("[AuthContext]", message, meta || "");
}

function shortId(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  if (s.length <= 12) return s;
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

function applyApplicationLanguage(language) {
  const lang = normalizeLanguage(language);

  /**
   * Language storage policy
   * -----------------------
   * AuthContext intentionally does not write language to localStorage or
   * sessionStorage. Durable preference belongs to the backend user profile.
   * The browser language is used only for the anonymous/public shell before an
   * authenticated profile exists.
   */
  if (i18n.language !== lang) {
    i18n.changeLanguage(lang);
  }

  return lang;
}

function applyPreferredLanguage(preferredLanguage) {
  return applyApplicationLanguage(preferredLanguage);
}

const LOGIN_LANGUAGE_MODES = Object.freeze({
  PROFILE: "profile",
  MANUAL: "manual",
});

function normalizeLoginLanguagePreference(preference = {}) {
  const mode = String(preference?.languageMode || "").trim().toLowerCase();

  if (mode === LOGIN_LANGUAGE_MODES.MANUAL) {
    return {
      languageMode: LOGIN_LANGUAGE_MODES.MANUAL,
      manualLanguage: normalizeLanguage(preference?.manualLanguage),
    };
  }

  return {
    languageMode: LOGIN_LANGUAGE_MODES.PROFILE,
    manualLanguage: null,
  };
}

function applyLoginLanguagePreference(user, preference) {
  const normalizedPreference = normalizeLoginLanguagePreference(preference);

  if (normalizedPreference.languageMode === LOGIN_LANGUAGE_MODES.MANUAL) {
    return applyApplicationLanguage(normalizedPreference.manualLanguage);
  }

  return applyPreferredLanguage(user?.preferred_language);
}

/* -------------------------------------------------------------------------- */
/* Cross-tab BroadcastChannel                                                 */
/* -------------------------------------------------------------------------- */

/**
 * In the strict tab-selected variant we still keep BroadcastChannel,
 * but ONLY for scoped logout synchronization.
 *
 * We explicitly do NOT use it for:
 * - session discovery
 * - parent/source recovery
 * - duplicate tab recovery
 * - session sharing
 *
 * BroadcastChannel is only a UX accelerator here:
 * it helps sibling tabs collapse quickly after same-session logout.
 * It does NOT create or restore auth state.
 */
const AUTH_BC_NAME = "hb_auth_channel";

const AUTH_EVENTS = Object.freeze({
  LOGOUT_SCOPE: "LOGOUT_SCOPE",
});

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

function getAppRootRedirectTarget() {
  const base = process.env.PUBLIC_URL || "";
  if (!base) return "/";
  return base.endsWith("/") ? base : `${base}/`;
}

function redirectToAppRoot() {
  window.location.href = getAppRootRedirectTarget();
}

function normalizeId(v) {
  const s = String(v ?? "").trim();
  return s || "";
}

function makeAuthError(err, fallbackMessage) {
  const status = err?.response?.status;
  const serverMsg =
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    "";
  const serverCode = err?.response?.data?.code || err?.code || "";

  let message = fallbackMessage;

  if (DEV) {
    if (serverMsg) {
      message = `${fallbackMessage} (HTTP ${status || "?"}): ${serverMsg}`;
    } else if (err?.message) {
      message = `${fallbackMessage}: ${err.message}`;
    }
  } else {
    if (status === 401) message = "Invalid username or password.";
    if (status === 403) message = "Your account is not approved or is inactive.";
  }

  const e = new Error(message);
  if (typeof status === "number") e.status = status;
  if (serverCode) e.code = serverCode;
  return e;
}

/**
 * Decide whether a scoped logout event applies to THIS TAB.
 *
 * Matching strategy:
 * 1) Prefer exact session selector match whenever both sides have sessionId.
 * 2) Fall back to userId ONLY when THIS TAB has no local session selector,
 *    but is still considered authenticated for UI state reasons.
 *
 * Why this stricter behavior matters:
 * - once a tab has been locally cleared, it must stop behaving as if it still
 *   belongs to its previous concrete session
 * - broad matching by userId alone can make already-logged-out tabs react to
 *   later logout events for other tabs of the same historical user
 *
 * This is also important for multi-user-on-one-device scenarios.
 * SessionId matching is what keeps one user's logout from accidentally
 * collapsing another user's still-valid tab.
 */
function shouldApplyScopedLogoutBroadcast(payload, currentUserId, currentSessionId) {
  if (!payload || payload.type !== AUTH_EVENTS.LOGOUT_SCOPE) return false;

  const payloadUserId = normalizeId(payload.userId);
  const payloadSessionId = normalizeId(payload.sessionId);

  const currentUid = normalizeId(currentUserId);
  const currentSid = normalizeId(currentSessionId);

  if (payloadSessionId && currentSid) {
    return payloadSessionId === currentSid;
  }

  if (!currentSid && payloadUserId && currentUid) {
    return payloadUserId === currentUid;
  }

  return false;
}

/* -------------------------------------------------------------------------- */
/* Provider                                                                   */
/* -------------------------------------------------------------------------- */

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null,
  });

  const [authChecked, setAuthChecked] = useState(false);
  const [accessToken, setAccessTokenState] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Compatibility field for older LoginPage code.
   *
   * In this strict tab-selected variant it is intentionally ALWAYS false.
   */
  const [canOfferRecoveredSessionUi] = useState(false);

  const [deviceId] = useState(() => getDeviceId());
  const [browserId, setBrowserIdState] = useState(() => getBrowserId());

  /**
   * IMPORTANT:
   * getCurrentSessionSnapshot() may already trigger duplicate-tab repair
   * inside sessionIdentity.js.
   *
   * That is a GOOD thing.
   *
   * It means the very first AuthContext read of tab state already benefits
   * from hardened sessionStorage clone detection.
   */
  const [tabSessionId, setTabSessionId] = useState(
    () => getCurrentSessionSnapshot().sessionId || ""
  );

  const bcRef = useRef(null);
  const clearLocalAuthRef = useRef(null);

  /**
   * Refs are needed because BroadcastChannel and token update callback
   * are long-lived handlers. They must see the latest state immediately.
   *
   * Why this matters:
   * - React state updates are asynchronous
   * - logout broadcast may arrive between local cleanup and the next render
   * - token update callback may fire while old render values are still visible
   */
  const currentUserIdRef = useRef("");
  const currentSessionIdRef = useRef("");
  const isAuthenticatedRef = useRef(false);

  useEffect(() => {
    currentUserIdRef.current = normalizeId(authState.user?.id);
    isAuthenticatedRef.current = Boolean(authState.isAuthenticated);

    dlog("auth refs updated from authState", {
      userId: currentUserIdRef.current || "",
      isAuthenticated: isAuthenticatedRef.current,
    });
  }, [authState.user, authState.isAuthenticated]);

  useEffect(() => {
    currentSessionIdRef.current = normalizeId(tabSessionId);

    dlog("currentSessionIdRef updated", {
      tabSessionId: shortId(currentSessionIdRef.current),
      tabId: shortId(getTabId()),
    });
  }, [tabSessionId]);

  /* ------------------------------------------------------------------------ */
  /* Broadcast helpers                                                        */
  /* ------------------------------------------------------------------------ */

  const broadcast = useCallback((type, payload = {}) => {
    const bc = bcRef.current;
    if (!bc) {
      dlog("broadcast skipped because BroadcastChannel is not ready", { type });
      return;
    }

    try {
      const message = {
        type,
        ...payload,
        ts: Date.now(),
      };

      dlog("broadcasting message", {
        type,
        payload: {
          ...payload,
          sessionId: shortId(payload?.sessionId),
        },
      });

      bc.postMessage(message);
    } catch (err) {
      dwarn("broadcast failed", {
        type,
        error: err?.message || String(err),
      });
    }
  }, []);

  /* ------------------------------------------------------------------------ */
  /* BroadcastChannel lifecycle                                               */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") {
      dwarn("BroadcastChannel not available; cross-tab logout sync disabled");
      return undefined;
    }

    const bc = new BroadcastChannel(AUTH_BC_NAME);
    bcRef.current = bc;

    dlog("BroadcastChannel opened", {
      channel: AUTH_BC_NAME,
      tabId: shortId(getTabId()),
    });

    bc.onmessage = (evt) => {
      const data = evt?.data || {};

      dlog("BroadcastChannel message received", {
        type: data.type,
        sessionId: shortId(data.sessionId),
        userId: data.userId || "",
        localTabId: shortId(getTabId()),
      });

      if (data.type !== AUTH_EVENTS.LOGOUT_SCOPE) {
        return;
      }

      if (!isAuthenticatedRef.current) {
        dlog("ignoring scoped logout because this tab is already unauthenticated", {
          localTabId: shortId(getTabId()),
        });
        return;
      }

      const shouldApply = shouldApplyScopedLogoutBroadcast(
        data,
        currentUserIdRef.current,
        currentSessionIdRef.current
      );

      dlog("scoped logout broadcast evaluated", {
        shouldApply,
        currentUserId: currentUserIdRef.current || "",
        currentSessionId: shortId(currentSessionIdRef.current),
        payloadUserId: data.userId || "",
        payloadSessionId: shortId(data.sessionId),
      });

      if (!shouldApply) return;

      dwarn("received scoped logout broadcast for this tab", {
        tabId: shortId(getTabId()),
      });

      if (typeof clearLocalAuthRef.current === "function") {
        clearLocalAuthRef.current();
      }

      redirectToAppRoot();
    };

    return () => {
      try {
        bc.close();
      } catch {
        // ignore
      }

      bcRef.current = null;

      dlog("BroadcastChannel closed", {
        channel: AUTH_BC_NAME,
        tabId: shortId(getTabId()),
      });
    };
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Identity sync                                                            */
  /* ------------------------------------------------------------------------ */

  const ensureClientIdentity = useCallback(() => {
    const id = getBrowserId();
    setBrowserIdState(id);

    dlog("client identity ensured", {
      deviceId: shortId(deviceId),
      browserId: shortId(id),
      tabId: shortId(getTabId()),
    });
  }, [deviceId]);

  /* ------------------------------------------------------------------------ */
  /* Token application                                                        */
  /* ------------------------------------------------------------------------ */

  const applyAccessToken = useCallback((token) => {
    const v = typeof token === "string" ? token.trim() : "";

    if (!v) {
      dlog("clearing access token from AuthContext");
      setAccessTokenState(null);
      setAuthToken(null);
      return;
    }

    dlog("applying access token in AuthContext", {
      tokenLength: v.length,
    });

    setAccessTokenState(v);
    setAuthToken(v);
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Session sync                                                             */
  /* ------------------------------------------------------------------------ */

  /**
   * Synchronize tab-local session selector and refs.
   *
   * This is one of the most important places for maintaining:
   * - proper X-Session-Id routing
   * - correct logout scoping
   * - correct socket re-auth after login/bootstrap
   *
   * Important architectural rule:
   * session identity is created only from authoritative backend results,
   * never from frontend guesses.
   */
  const syncSessionIdentity = useCallback((sessionId, userId) => {
    const sid = normalizeId(sessionId);
    const uid = normalizeId(userId);

    if (!sid) {
      dwarn("syncSessionIdentity skipped because sessionId is empty", {
        userId: uid || "",
      });
      return;
    }

    dlog("syncSessionIdentity called", {
      sessionId: shortId(sid),
      userId: uid || "",
      tabId: shortId(getTabId()),
    });

    setSessionIdentity({
      sessionId: sid,
      userId: uid,
    });

    setTabSessionId(sid);

    currentSessionIdRef.current = sid;
    if (uid) currentUserIdRef.current = uid;
    isAuthenticatedRef.current = true;
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Local cleanup                                                            */
  /* ------------------------------------------------------------------------ */

  /**
   * Clear local auth state for THIS TAB only.
   *
   * IMPORTANT:
   * - this must not directly log out sibling tabs
   * - this must not call backend logout
   * - this is the safe local collapse mechanism used by:
   *   - token loss
   *   - refresh failure
   *   - bootstrap failure
   *   - "login as another user in this tab"
   *
   * SECURITY NOTE:
   * This must be synchronous enough for long-lived callbacks to stop treating
   * the tab as authenticated immediately.
   */
  const clearLocalAuth = useCallback(() => {
    const snapshot = getCurrentSessionSnapshot();

    dwarn("clearLocalAuth called", {
      snapshot: {
        sessionId: shortId(snapshot.sessionId),
        userId: snapshot.userId || "",
        tabId: shortId(snapshot.tabId),
      },
    });

    currentUserIdRef.current = "";
    currentSessionIdRef.current = "";
    isAuthenticatedRef.current = false;

    setAuthState({ isAuthenticated: false, user: null });
    setAccessTokenState(null);
    setAuthToken(null);
    setTabSessionId("");

    clearTabSessionIdentity();
    socketClient.disconnect();
  }, []);

  useEffect(() => {
    clearLocalAuthRef.current = clearLocalAuth;
  }, [clearLocalAuth]);

  /* ------------------------------------------------------------------------ */
  /* Fresh login in THIS TAB                                                  */
  /* ------------------------------------------------------------------------ */

  /**
   * Prepare THIS TAB for logging in as another user.
   *
   * In strict tab-selected auth this is intentionally simple:
   * - clear only this tab locally
   * - do not attempt any restore/recovery later
   *
   * This is the correct behavior for shared-device scenarios.
   */
  const prepareForLoginInThisTab = useCallback(() => {
    const snapshot = getCurrentSessionSnapshot();

    dwarn("prepareForLoginInThisTab called", {
      tabId: shortId(snapshot.tabId),
      sessionId: shortId(snapshot.sessionId),
      userId: snapshot.userId || "",
    });

    clearLocalAuth();
  }, [clearLocalAuth]);

  /* ------------------------------------------------------------------------ */
  /* softLogout                                                               */
  /* ------------------------------------------------------------------------ */

  /**
   * Local logout-like fallback used by auth-failure flows.
   *
   * It intentionally:
   * - clears only THIS tab locally
   * - redirects to app root
   *
   * It does not call backend logout.
   *
   * Typical reasons:
   * - refresh failed
   * - session selector missing/invalid
   * - strict backend refused current tab bootstrap
   */
  const softLogout = useCallback(
    (reason = "AUTH_INVALID") => {
      dwarn("softLogout called", {
        reason,
        tabId: shortId(getTabId()),
      });

      clearLocalAuth();
      redirectToAppRoot();
    },
    [clearLocalAuth]
  );

  /* ------------------------------------------------------------------------ */
  /* apiClient -> AuthContext integration                                     */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    setOnTokenUpdate((tokenOrNull) => {
      dlog("received token update from apiClient", {
        hasToken: Boolean(tokenOrNull),
        tokenLength: tokenOrNull ? String(tokenOrNull).length : 0,
      });

      applyAccessToken(tokenOrNull);

      if (!tokenOrNull) {
        clearLocalAuth();
        return;
      }

      /**
       * Read session snapshot AFTER token update.
       *
       * Important:
       * apiClient may already have updated local session identity during refresh,
       * and sessionIdentity.js may also have performed duplicate-tab repair earlier.
       */
      const snapshot = getCurrentSessionSnapshot();
      setTabSessionId(snapshot.sessionId || "");

      dlog("tabSessionId refreshed from token update", {
        sessionId: shortId(snapshot.sessionId),
        userId: snapshot.userId || "",
        tabId: shortId(snapshot.tabId),
      });
    });

    return () => setOnTokenUpdate(null);
  }, [applyAccessToken, clearLocalAuth]);

  /* ------------------------------------------------------------------------ */
  /* Bootstrap                                                                */
  /* ------------------------------------------------------------------------ */

  /**
   * Bootstrap auth for this SPA tab.
   *
   * STRICT TAB-SELECTED POLICY:
   * ---------------------------
   * We do NOT:
   * - discover sessions from other tabs
   * - restore from duplicate/source lineage
   * - rehydrate from registry
   * - offer chooser UI
   * - ask backend to "figure out" current tab context without local sessionId
   *
   * We do:
   * 1) ensure client identity
   * 2) check whether THIS TAB already has its own explicit local sessionId
   * 3) only then call backend /auth/check-auth
   * 4) accept backend answer as final authority
   *
   * CRITICAL DESIGN POINT:
   * If THIS TAB has no local session selector, bootstrap must stop locally
   * and leave the tab unauthenticated.
   *
   * This is what guarantees:
   * - duplicate tab -> login
   * - manual URL -> login
   * - new fresh tab -> login
   *
   * IMPORTANT INTERACTION WITH sessionIdentity.js
   * ---------------------------------------------
   * getSessionId() and getCurrentSessionSnapshot() may already repair cloned
   * sessionStorage before we make any auth decision here.
   *
   * That means duplicate-tab detection happens BELOW this layer,
   * and AuthContext simply respects the repaired result:
   *
   * - repaired duplicate tab -> no local session id -> skip bootstrap -> login
   */
  const initializeAuthState = useCallback(async () => {
    const initialSnapshot = getCurrentSessionSnapshot();

    dlog("initializeAuthState started", {
      tabId: shortId(initialSnapshot.tabId),
      sessionId: shortId(initialSnapshot.sessionId),
      userId: initialSnapshot.userId || "",
    });

    setLoading(true);
    setAuthChecked(false);
    ensureClientIdentity();

    try {
      /**
       * Important:
       * getSessionId() may already trigger duplicate-tab repair in sessionIdentity.js.
       * After that repair, cloned tabs should have no local session id anymore.
       */
      const localSessionId = normalizeId(getSessionId());
      const hasLocalAuthority = Boolean(localSessionId && hasTabSessionIdentity());

      dlog("initializeAuthState local authority check", {
        tabId: shortId(getTabId()),
        localSessionId: shortId(localSessionId),
        hasLocalAuthority,
      });

      /**
       * STRICT MODE:
       * If this tab does not already own a local session selector,
       * it must NOT call /auth/check-auth.
       *
       * Why?
       * Because backend should never be asked to infer a session for a tab
       * that has not explicitly logged in yet.
       */
      if (!hasLocalAuthority) {
        dlog("initializeAuthState skipping /auth/check-auth because tab has no local session authority", {
          tabId: shortId(getTabId()),
        });

        clearLocalAuth();
        return;
      }

      dlog("calling /auth/check-auth", {
        tabId: shortId(getTabId()),
        sessionIdBeforeCheck: shortId(localSessionId),
      });

      const res = await apiClient.get("/auth/check-auth", {
        withCredentials: true,
      });

      dlog("/auth/check-auth returned", {
        status: res?.status,
        hasUser: Boolean(res?.data?.user),
        serverSessionId: shortId(res?.data?.session?.id),
      });

      if (res?.status === 200 && res.data?.user) {
        const user = res.data.user || null;
        const sessionIdFromServer = normalizeId(res.data?.session?.id);
        const snapshotAfterCheck = getCurrentSessionSnapshot();

        /**
         * Prefer server session id.
         * Fallback to already existing tab-local selector if it exists.
         *
         * In practice, under strict mode these should usually match.
         */
        const effectiveSessionId =
          sessionIdFromServer || normalizeId(snapshotAfterCheck.sessionId);

        dlog("check-auth success path", {
          userId: user?.id || "",
          serverSessionId: shortId(sessionIdFromServer),
          localSessionId: shortId(snapshotAfterCheck.sessionId),
          effectiveSessionId: shortId(effectiveSessionId),
          tabId: shortId(snapshotAfterCheck.tabId),
        });

        if (user && effectiveSessionId) {
          syncSessionIdentity(effectiveSessionId, user.id);
        } else {
          /**
           * Extremely defensive fallback:
           * keep tabSessionId aligned with the snapshot if backend returned
           * authenticated user but omitted session payload.
           */
          setTabSessionId(snapshotAfterCheck.sessionId || "");
        }

        applyPreferredLanguage(user?.preferred_language);

        setAuthState({
          isAuthenticated: true,
          user,
        });

        if (effectiveSessionId) {
          dlog("refreshing socket auth after successful bootstrap", {
            sessionId: shortId(effectiveSessionId),
          });
          socketClient.refreshAuthAndReconnect();
        }

        return;
      }

      dwarn("check-auth returned non-authenticated result; clearing local auth");
      clearLocalAuth();
    } catch (err) {
      dwarn("initializeAuthState failed", {
        message: err?.message || String(err),
        code: err?.code || "",
        status: err?.response?.status,
      });

      clearLocalAuth();
    } finally {
      const finalSnapshot = getCurrentSessionSnapshot();

      dlog("initializeAuthState finished", {
        finalSnapshot: {
          sessionId: shortId(finalSnapshot.sessionId),
          userId: finalSnapshot.userId || "",
          tabId: shortId(finalSnapshot.tabId),
        },
      });

      setAuthChecked(true);
      setLoading(false);
    }
  }, [ensureClientIdentity, syncSessionIdentity, clearLocalAuth]);

  /* ------------------------------------------------------------------------ */
  /* Login                                                                    */
  /* ------------------------------------------------------------------------ */

  /**
   * Explicit user login in THIS TAB.
   *
   * Security semantics:
   * - no recovery behavior is revived
   * - chooser UI remains disabled
   * - tab-local session selector is synchronized from authoritative server result
   * - this tab becomes routable only after backend explicitly returns sessionId
   *
   * Public language behavior:
   * - LoginPage may pass { languageMode: "profile" } as the default behavior,
   *   which means use user.preferred_language from the authenticated profile,
   * - LoginPage may pass { languageMode: "manual", manualLanguage: "pl"|"en" },
   *   which means keep that explicit language for the current SPA session,
   * - neither branch writes the login language choice to browser storage.
   */
  const handleLogin = useCallback(
    async (username, password, languagePreference = {}) => {
      const before = getCurrentSessionSnapshot();

      dlog("handleLogin started", {
        username: username || "",
        tabId: shortId(before.tabId),
        snapshotBefore: {
          sessionId: shortId(before.sessionId),
          userId: before.userId || "",
        },
      });

      setLoading(true);
      ensureClientIdentity();

      try {
        const res = await apiClient.post(
          "/auth/login",
          { username, password },
          { withCredentials: true }
        );

        const token = res.data?.accessToken || null;
        const user = res.data?.user || null;
        const sessionIdFromServer = normalizeId(res.data?.sessionId);

        dlog("handleLogin response received", {
          hasToken: Boolean(token),
          tokenLength: token ? String(token).length : 0,
          userId: user?.id || "",
          sessionIdFromServer: shortId(sessionIdFromServer),
        });

        if (user && sessionIdFromServer) {
          syncSessionIdentity(sessionIdFromServer, user.id);
        } else {
          dwarn("login succeeded but sessionId/user missing", {
            hasUser: Boolean(user),
            hasSessionId: Boolean(sessionIdFromServer),
          });
        }

        applyAccessToken(token);

        if (user) {
          applyLoginLanguagePreference(user, languagePreference);
          setAuthState({ isAuthenticated: true, user });
        } else {
          await initializeAuthState();

          /**
           * Extremely defensive fallback. A successful login should normally
           * return user data, but if an older/backend edge response required
           * bootstrap, still respect a manual LoginPage language override after
           * bootstrap has completed.
           */
          const normalizedPreference =
            normalizeLoginLanguagePreference(languagePreference);
          if (
            normalizedPreference.languageMode === LOGIN_LANGUAGE_MODES.MANUAL
          ) {
            applyApplicationLanguage(normalizedPreference.manualLanguage);
          }
        }

        if (sessionIdFromServer) {
          dlog("refreshing socket auth after login", {
            sessionId: shortId(sessionIdFromServer),
          });
          socketClient.refreshAuthAndReconnect();
        }

        const after = getCurrentSessionSnapshot();

        dlog("handleLogin finished successfully", {
          snapshotAfter: {
            sessionId: shortId(after.sessionId),
            userId: after.userId || "",
            tabId: shortId(after.tabId),
          },
        });

        return true;
      } catch (err) {
        dwarn("handleLogin failed", {
          message: err?.message || String(err),
          code: err?.code || "",
          status: err?.response?.status,
        });

        clearLocalAuth();
        throw makeAuthError(err, "Login failed.");
      } finally {
        setLoading(false);
      }
    },
    [
      ensureClientIdentity,
      syncSessionIdentity,
      applyAccessToken,
      initializeAuthState,
      clearLocalAuth,
    ]
  );

  /* ------------------------------------------------------------------------ */
  /* Explicit logout                                                          */
  /* ------------------------------------------------------------------------ */

  /**
   * Explicit user logout for THIS current session.
   *
   * IMPORTANT ORDER:
   * 1) best-effort backend logout
   * 2) broadcast scoped logout by sessionId
   * 3) clear THIS TAB locally
   * 4) redirect
   *
   * This order preserves:
   * - correct same-session logout propagation
   * - multi-user safety on one device
   *
   * Important architectural note:
   * if many tabs explicitly logged into the same backend tuple session,
   * they will share the same sessionId and therefore all collapse together,
   * which is exactly the requested behavior.
   */
  const handleLogout = useCallback(async () => {
    const snapshot = getCurrentSessionSnapshot();
    const currentUserId =
      normalizeId(authState.user?.id) || normalizeId(snapshot.userId);

    dlog("handleLogout started", {
      currentUserId,
      snapshot: {
        sessionId: shortId(snapshot.sessionId),
        userId: snapshot.userId || "",
        tabId: shortId(snapshot.tabId),
      },
    });

    try {
      await apiClient.post("/auth/logout", null, {
        withCredentials: true,
      });

      dlog("backend logout completed");
    } catch (err) {
      dwarn("logout best-effort failed", {
        message: err?.message || String(err),
        code: err?.code || "",
        status: err?.response?.status,
      });
    } finally {
      broadcast(AUTH_EVENTS.LOGOUT_SCOPE, {
        userId: currentUserId,
        sessionId: snapshot.sessionId,
      });

      clearLocalAuth();
      redirectToAppRoot();
    }
  }, [authState.user, broadcast, clearLocalAuth]);

  /* ------------------------------------------------------------------------ */
  /* Other actions                                                            */
  /* ------------------------------------------------------------------------ */

  const registerUser = useCallback(async (username, password, firstName, lastName) => {
    setLoading(true);

    try {
      const res = await apiClient.post(
        "/auth/register",
        {
          username,
          password,
          first_name: firstName,
          last_name: lastName,
        },
        { withCredentials: true }
      );

      return res.data;
    } catch (err) {
      throw makeAuthError(err, "Registration failed.");
    } finally {
      setLoading(false);
    }
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      const res = await apiClient.put(
        "/auth/change-password",
        { currentPassword, newPassword },
        { withCredentials: true }
      );

      return res.data;
    } catch (err) {
      throw makeAuthError(err, "Change password failed.");
    }
  }, []);

  /**
   * Update the preferred_language field of the current auth user snapshot.
   *
   * Default behavior intentionally preserves the historical contract: calling
   * updateCurrentUserLanguage("pl") also applies PL to the current runtime UI.
   *
   * Batch 13 adds a stricter split between:
   * - current displayed language, owned by the sidebar LanguageSwitcher, and
   * - durable profile language, owned by profile/user settings.
   *
   * For pure profile updates, callers pass:
   *
   *   updateCurrentUserLanguage(lang, { applyToRuntime: false })
   *
   * This updates authState.user.preferred_language without changing what is
   * currently displayed on screen. That keeps a manual login/session override
   * from being silently replaced by a profile edit.
   */
  const updateCurrentUserLanguage = useCallback((preferredLanguage, options = {}) => {
    const lang = normalizeLanguage(preferredLanguage);
    const applyToRuntime = options?.applyToRuntime !== false;

    if (applyToRuntime) {
      applyPreferredLanguage(lang);
    }

    setAuthState((prev) => {
      if (!prev?.user) return prev;
      return {
        ...prev,
        user: {
          ...prev.user,
          preferred_language: lang,
        },
      };
    });

    return lang;
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Diagnostics                                                              */
  /* ------------------------------------------------------------------------ */

  const hasTabSessionId = useMemo(() => Boolean(tabSessionId), [tabSessionId]);

  /* ------------------------------------------------------------------------ */
  /* Bootstrap once                                                           */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    initializeAuthState();
  }, [initializeAuthState]);

  /* ------------------------------------------------------------------------ */
  /* Context value                                                            */
  /* ------------------------------------------------------------------------ */

  const value = useMemo(
    () => ({
      authState,
      authChecked,
      loading,

      deviceId,
      browserId,
      accessToken,
      tabSessionId,
      hasTabSessionId,

      /**
       * Compatibility field.
       * In the strict tab-selected variant it is always false.
       */
      canOfferRecoveredSessionUi,

      initializeAuthState,
      handleLogin,
      handleLogout,
      softLogout,
      prepareForLoginInThisTab,

      registerUser,
      changePassword,
      updateCurrentUserLanguage,

      clearLocalAuth,
    }),
    [
      authState,
      authChecked,
      loading,
      deviceId,
      browserId,
      accessToken,
      tabSessionId,
      hasTabSessionId,
      canOfferRecoveredSessionUi,
      initializeAuthState,
      handleLogin,
      handleLogout,
      softLogout,
      prepareForLoginInThisTab,
      registerUser,
      changePassword,
      updateCurrentUserLanguage,
      clearLocalAuth,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;



